import React, { useContext, useEffect, useState } from "react";
import { useAuthQuery as useQuery } from '../../../../shared/hooks/useAuthQuery';
import { api } from "../../../../convex/_generated/api";
import { useConvexAuth, useMutation } from "convex/react";
import { Button } from "../../../../catalyst-components/button";
import { useNavigate } from "react-router-dom";
import { AdminRoutes, Assistant, MainRoutes } from "../../../../shared/models";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../catalyst-components/table";
import { BaseContext } from "../../../../shared/base-context";
import { Id } from "../../../../convex/_generated/dataModel";
import { Alert, AlertTitle, AlertDescription, AlertActions } from "../../../../catalyst-components/alert";
import { ConfirmationDialog } from "../../../../catalyst-components/confirmation-dialog";
import { Input } from "../../../../catalyst-components/input";
import { Select } from "../../../../catalyst-components/select";
import { useStableQuery } from "../../../../shared/hooks/useStableQuery";

// Helper function to escape CSV values
const escapeCSVValue = (value: string | undefined | null): string => {
    if (value === undefined || value === null) return '';
    const stringValue = String(value);
    // If value contains comma, newline, or double quote, wrap in quotes and escape internal quotes
    if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
};

export const Assistants: React.FC = () => {

    const { isLoading, isAuthenticated } = useConvexAuth();

    const { convexUserId } = useContext(BaseContext);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [success, setSuccess] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState<{
        open: boolean;
        assistant?: { _id: Id<"assistants">; name: string; is_active: boolean };
    }>({ open: false });
    
    // Filter states
    const [nameFilter, setNameFilter] = useState('');
    const [displayNameFilter, setDisplayNameFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [filteredAssistants, setFilteredAssistants] = useState<Assistant[]>([]);
    
    const navigate = useNavigate();
   
    // Queries
    const assistants = useStableQuery(api.assistants.list, convexUserId ? { user_id: convexUserId as Id<"users"> } : 'skip') || [];

    // Get unique types and statuses for filter options
    const uniqueTypes = Array.from(new Set(assistants.map(a => a.typeName)));
    const statusOptions = ['All', 'Disabled', 'Draft', 'Testable'];

    const clearFilters = () => {
        setNameFilter('');
        setDisplayNameFilter('');
        setTypeFilter('');
        setStatusFilter('');
    };

    // // Filtered assistants
    // const filteredAssistants = assistants.filter(assistant => {

    //     let matchesName = assistant.name.toLowerCase().includes(nameFilter.toLowerCase());
    //     matchesName = assistant.name.toLowerCase().includes(nameFilter.toLowerCase());
    //     let matchesDisplayName = assistant.display_name?.toLowerCase().includes(displayNameFilter.toLowerCase());
    //     matchesDisplayName = assistant.display_name?.toLowerCase().includes(displayNameFilter.toLowerCase());
    //     let matchesType = !typeFilter || assistant.typeName === typeFilter;
        
    //     const matchesStatus = !statusFilter || statusFilter === 'All' || 
    //         (statusFilter === 'Disabled' && !assistant.is_active) ||
    //         (statusFilter === 'Draft' && assistant.is_draft) ||
    //         (statusFilter === 'Testable' && assistant.is_active && !assistant.is_draft);
        
    //     return matchesName && matchesDisplayName && matchesType && matchesStatus;
    // });

    // Mutations
    const completeSessionByAssistantId = useMutation(api.sessions.completeSessionByAssistantId);
    const toggleAssistantActive = useMutation(api.assistants.toggleActive);
    const createSession = useMutation(api.sessions.startSession);

    useEffect(() => {
        let filteredAssistants = assistants;
        if (nameFilter) {
            filteredAssistants = filteredAssistants.filter(assistant => assistant.name.toLowerCase().includes(nameFilter.toLowerCase()));
        }
        if (displayNameFilter) {
            filteredAssistants = filteredAssistants.filter(assistant => assistant.display_name?.toLowerCase().includes(displayNameFilter.toLowerCase()));
        }
        if (typeFilter) {
            filteredAssistants = filteredAssistants.filter(assistant => assistant.typeName === typeFilter);
        }
        if (statusFilter && statusFilter !== 'All') {
            filteredAssistants = filteredAssistants.filter(assistant => {
                switch (statusFilter) {
                    case 'Disabled':
                        return !assistant.is_active;
                    case 'Draft':
                        return assistant.is_draft;
                    case 'Testable':
                        return assistant.is_active && !assistant.is_draft;
                    default:
                        return true;
                }
            });
        }
        setFilteredAssistants(filteredAssistants);
    }, [nameFilter, displayNameFilter, typeFilter, statusFilter, assistants]);

    useEffect(() => {
        if (!isAuthenticated) {
            navigate(MainRoutes.Landing);
        }
    }, [isAuthenticated]);

    const handleToggleActive = async (assistant: { _id: Id<"assistants">; name: string; is_active: boolean }) => {
        if (assistant.is_active) {
            setConfirmDialog({ open: true, assistant });
        } else {
            try {
                await toggleAssistantActive({
                    id: assistant._id,
                    is_active: true
                });
                setAlertMessage('Assistant enabled successfully!');
                setShowAlert(true);
                setSuccess(true);
            } catch (error) {
                setAlertMessage('Error updating assistant. Please try again.');
                setShowAlert(true);
                setSuccess(false);
            }
        }
    };

    const handleConfirmDisable = async () => {
        if (!confirmDialog.assistant) return;
        
        try {
            await toggleAssistantActive({
                id: confirmDialog.assistant._id,
                is_active: false
            });
            setAlertMessage('Assistant disabled successfully!');
            setShowAlert(true);
            setSuccess(true);
        } catch (error) {
            setAlertMessage('Error updating assistant. Please try again.');
            setShowAlert(true);
            setSuccess(false);
        }
    };

    const newSession = async (assistantId: Id<"assistants">) => {
        await completeSessionByAssistantId({
           assistantId,
           user_id: convexUserId as Id<"users">
        });

        const newSession = await createSession({
            assistant_id: assistantId,
            user_id: convexUserId as Id<"users">
        });

        navigate(`${MainRoutes.Chat}?sessionId=${newSession.session_id}&testing=true`);
    }

    const exportAssistantsToCSV = () => {
        if (!assistants || assistants.length === 0) return;

        const publishedAssistants = assistants.filter(a => a.is_draft === false);
        if (publishedAssistants.length === 0) return;

        // CSV headers
        const headers = ['Display Name', 'Description', 'Greeting', 'Prompt'];

        // Build CSV content
        const csvRows = [
            headers.join(','),
            ...publishedAssistants.map(assistant => [
                escapeCSVValue(assistant.display_name),
                escapeCSVValue(assistant.description),
                escapeCSVValue(assistant.greeting),
                escapeCSVValue(assistant.prompt)
            ].join(','))
        ];

        const csvContent = csvRows.join('\n');

        // Create and download the file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `assistants_export_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }
    return (
        <section className="min-h-screen bg-background pt-24 px-6 flex flex-col justify-start">
            <h1 className="text-3xl font-bold mb-4">Assistants</h1>
            <div className="flex flex-row gap-4 mb-6"> 
                <Button color="light" onClick={() => navigate('../')}>
                    Back to admin
                </Button>
            </div>
            <p className="text-gray-600 mb-4">
                Here you can manage your AI assistants. View and edit their details, start new chat sessions, or create new assistants.
                Click "View Details" to configure an assistant's personality and behavior, or "New Session" to start chatting with them.
            </p>
            <div className="flex gap-4 mt-4 mb-4">
                <Button color="light" className="w-72" onClick={() => navigate(`${AdminRoutes.Assistants}/new`)}>Add New Assistant</Button>
                <Button
                    color="light"
                    onClick={exportAssistantsToCSV}
                    disabled={!assistants || assistants.length === 0}
                >
                    Export All Testable Assistants to CSV
                </Button>
            </div>
            
            {/* Filter Controls */}
            <div className="flex flex-wrap gap-4 mb-6">
                <div className="w-64">
                    <Input
                        type="text"
                        placeholder="Filter by display name"
                        value={displayNameFilter}
                        onChange={(e) => setDisplayNameFilter(e.target.value)}
                    />
                </div>
                <div className="w-64">
                    <Input
                        type="text"
                        placeholder="Filter by name"
                        value={nameFilter}
                        onChange={(e) => setNameFilter(e.target.value)}
                    />
                </div>
                <div className="w-48">
                    <Select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                    >
                        <option value="">All Types</option>
                        {uniqueTypes.map(type => (
                            <option key={type} value={type}>{type}</option>
                        ))}
                    </Select>
                </div>
                <div className="w-48">
                    <Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        {statusOptions.map(status => (
                            <option key={status} value={status}>{status}</option>
                        ))}
                    </Select>
                </div>
                <div>
                    <Button 
                        color="light" 
                        onClick={clearFilters}
                        disabled={!nameFilter && !typeFilter && !statusFilter && !displayNameFilter}
                    >
                        Clear Filters
                    </Button>
                </div>
            <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Showing {filteredAssistants.length} of {assistants.length} assistants</span>
            </div>
            </div>

            <Table>
                <TableHead>
                    <TableRow>
                        <TableHeader>Assistant Display Name</TableHeader>
                        <TableHeader>Assistant Name</TableHeader>
                        <TableHeader>Type</TableHeader>
                        <TableHeader>Status</TableHeader>
                        <TableHeader>Last Modified</TableHeader>
                        <TableHeader>Last Modified By</TableHeader>
                        <TableHeader>Actions</TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {filteredAssistants.sort((a, b) => Date.parse(a.last_modified_timestamp || '') - Date.parse(b.last_modified_timestamp || '') > 0 ? -1 : 1).map(assistant => (
                        <TableRow key={assistant._id}>
                            <TableCell>{assistant.display_name}</TableCell>
                            <TableCell>
                                <span className={assistant.is_active ? 'text-primary-5' : 'text-zinc-400'}>
                                    {assistant.name}
                                </span>
                            </TableCell>
                            <TableCell>{assistant.typeName}</TableCell>
                            <TableCell>
                                <div className="flex items-center gap-2">
                                    <div className={`w-2 h-2 rounded-full ${
                                        !assistant.is_active ? 'bg-red-500' :
                                        assistant.is_draft ? 'bg-gray-400' :
                                        'bg-green-500'
                                    }`} />
                                    <span className={
                                        !assistant.is_active ? 'text-zinc-400' :
                                        assistant.is_draft ? 'text-gray-400' :
                                        'text-primary-5'
                                    }>
                                        {!assistant.is_active ? 'Disabled' :
                                         assistant.is_draft ? 'Draft' :
                                         'Testable'}
                                    </span>
                                </div>
                            </TableCell>
                            <TableCell>{assistant.last_modified_timestamp ? new Date(assistant.last_modified_timestamp).toLocaleDateString() : ''}</TableCell>
                            <TableCell>{assistant.last_modified_by_name}</TableCell>
                            <TableCell>
                                <div className="flex gap-2">
                                    <Button color="light" onClick={() => navigate(`${AdminRoutes.Assistants}/${assistant._id}`)}>
                                        Edit
                                    </Button>
                                    <Button 
                                        color="light" 
                                        disabled={!assistant.is_active}
                                        onClick={() => newSession(assistant._id)}
                                    >
                                        New Session
                                    </Button>
                                    <Button
                                        color="light"
                                        onClick={() => handleToggleActive(assistant)}
                                    >
                                        {assistant.is_active ? 'Disable' : 'Enable'}
                                    </Button>
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <ConfirmationDialog
                open={confirmDialog.open}
                onClose={() => setConfirmDialog({ open: false })}
                onConfirm={handleConfirmDisable}
                isCritical={true}
                title="Disable Assistant"
                description={`Are you sure you want to disable ${confirmDialog.assistant?.name}? This will remove all interactions using this assistant.`}
                confirmText="Disable"
                cancelText="Cancel"
            />
            <Alert open={showAlert} onClose={() => setShowAlert(false)} size="lg">
                <AlertTitle>Notification</AlertTitle>
                <AlertDescription>{alertMessage}</AlertDescription>
                <AlertActions>
                    <Button plain onClick={() => setShowAlert(false)}>
                        Ok
                    </Button>
                </AlertActions>
            </Alert>
        </section>
    )
}